import type { Command, CommandContext, CommandResult } from "@/commands/Command";
import type { CurvaDocument } from "@/document/model/document";
import {
  cloneScriptEntryWithFreshIds,
  findScriptEntryPath,
  type ScriptEntry,
  type ScriptLibrary,
} from "@/document/model/documentScripts";
import { editorScriptsStore } from "@/editor/store/editorScriptsStore";
import {
  clearEditorScriptsDocument,
  createEmptyEditorScriptsDocument,
  EDITOR_SCRIPTS_DOCUMENT_NAME,
  saveEditorScriptsDocument,
} from "@/io/editorScriptsPersistence";
import { prepareScriptImport } from "@/document/operations/scriptImport";

function emptyResult(): CommandResult {
  return { changedNodeIds: [], affectedBounds: [] };
}

function cloneDoc(document: CurvaDocument | undefined): CurvaDocument | undefined {
  return document ? structuredClone(document) : undefined;
}

function applyEditorScriptsDocument(document: CurvaDocument | undefined): void {
  editorScriptsStore.setDocument(document);
  void (document ? saveEditorScriptsDocument(document) : clearEditorScriptsDocument()).catch(error => {
    console.warn("[curva] failed to persist editor scripts", error);
  });
}

/** Replace the global editor-scripts package with an imported .curva document. */
export class ReplaceEditorScriptsCommand implements Command {
  readonly id = "editorScripts.replace";
  readonly label = "Replace Editor Scripts";
  readonly affectsDocument = false;
  private before?: CurvaDocument;

  constructor(private readonly nextDocument: CurvaDocument) {}

  execute(_context: CommandContext): CommandResult {
    this.before = cloneDoc(editorScriptsStore.getDocument());
    const next = structuredClone(this.nextDocument);
    next.name = EDITOR_SCRIPTS_DOCUMENT_NAME;
    applyEditorScriptsDocument(next);
    return emptyResult();
  }

  undo(_context: CommandContext): CommandResult {
    applyEditorScriptsDocument(cloneDoc(this.before));
    return emptyResult();
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

/**
 * Merge scripts from an imported .curva into the editor-scripts package
 * (creates the package if missing). Wraps import in a named folder.
 */
export class AddEditorScriptsCommand implements Command {
  readonly id = "editorScripts.add";
  readonly label = "Add Editor Scripts";
  readonly affectsDocument = false;
  private before?: CurvaDocument;

  constructor(
    private readonly importedDocument: CurvaDocument,
    private readonly folderBaseName: string,
  ) {}

  execute(_context: CommandContext): CommandResult {
    this.before = cloneDoc(editorScriptsStore.getDocument());
    const base = this.before ? structuredClone(this.before) : createEmptyEditorScriptsDocument();
    const prepared = prepareScriptImport(base, this.importedDocument, this.folderBaseName);
    if (!prepared) {
      applyEditorScriptsDocument(base);
      return emptyResult();
    }
    base.scripts = base.scripts ?? { items: [] };
    base.scripts.items.push(structuredClone(prepared.folder));
    base.name = EDITOR_SCRIPTS_DOCUMENT_NAME;
    applyEditorScriptsDocument(base);
    return emptyResult();
  }

  undo(_context: CommandContext): CommandResult {
    applyEditorScriptsDocument(cloneDoc(this.before));
    return emptyResult();
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

/** Clear the global editor-scripts package. */
export class ClearEditorScriptsCommand implements Command {
  readonly id = "editorScripts.clear";
  readonly label = "Clear Editor Scripts";
  readonly affectsDocument = false;
  private before?: CurvaDocument;

  execute(_context: CommandContext): CommandResult {
    this.before = cloneDoc(editorScriptsStore.getDocument());
    applyEditorScriptsDocument(undefined);
    return emptyResult();
  }

  undo(_context: CommandContext): CommandResult {
    applyEditorScriptsDocument(cloneDoc(this.before));
    return emptyResult();
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

/**
 * Copy an editor-script entry (script or folder tree) into the active document library.
 * Dirties the document like a normal script import.
 */
export class CopyEditorScriptEntryToDocumentCommand implements Command {
  readonly id = "editorScripts.copyToDocument";
  readonly label = "Copy Script to Document";
  private beforeLibrary?: ScriptLibrary;
  private inserted?: ScriptEntry;

  constructor(private readonly entryId: string) {}

  execute(context: CommandContext): CommandResult {
    const library = context.document.scripts ?? { items: [] };
    this.beforeLibrary = structuredClone(library);

    const editorLibrary = editorScriptsStore.getLibrary();
    const located = findScriptEntryPath(editorLibrary, this.entryId);
    if (!located) {
      throw new Error(`Editor script "${this.entryId}" was not found.`);
    }

    this.inserted = cloneScriptEntryWithFreshIds(located.entry);
    library.items.push(this.inserted);
    context.document.scripts = library;
    return emptyResult();
  }

  undo(context: CommandContext): CommandResult {
    if (!this.beforeLibrary) {
      throw new Error("Cannot undo copy to document; snapshot missing.");
    }
    context.document.scripts = structuredClone(this.beforeLibrary);
    return emptyResult();
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}
